import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { History, RotateCcw, Check, Loader2, Trash2, Pencil } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Loading, Empty, handleErr, fmtDateTime } from "@/pages/admin/AdminUI";

const TABS = [["pending", "Can be restored"], ["restored", "Restored"], ["kept", "Kept permanently"]];

const Preview = ({ doc }) => {
  const [open, setOpen] = useState(false);
  if (!doc) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)} className="text-[11px] text-slate-400 hover:text-white underline">{open ? "Hide" : "Show"} saved copy</button>
      {open && <pre className="mt-2 max-h-56 overflow-auto text-[11px] text-slate-300 bg-[#0A0C10] border border-[#273042] rounded-lg p-3 whitespace-pre-wrap break-all">{JSON.stringify(doc, null, 2)}</pre>}
    </div>
  );
};

const Item = ({ it, onRestore, onKeep, busy }) => (
  <Card className="p-4" data-testid={`recovery-item-${it.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          {it.op === "delete" ? <Trash2 className="w-4 h-4 text-red-400" /> : <Pencil className="w-4 h-4 text-amber-400" />}
          <span className={`font-bold uppercase text-[11px] ${it.op === "delete" ? "text-red-400" : "text-amber-400"}`}>{it.op === "delete" ? "Deleted" : "Edited"}</span>
          <span className="text-white font-semibold truncate">{it.summary}</span>
          {it.count > 1 && <span className="text-[11px] text-slate-400">({it.count} items)</span>}
        </div>
        <div className="text-xs text-slate-400 mt-1">
          {it.collection} · by <b className="text-slate-200">{it.actor}</b> ({it.actorRole}) · {fmtDateTime(it.createdAt)}
          {it.resolvedAt && <> · {it.status} by {it.resolvedBy} {fmtDateTime(it.resolvedAt)}</>}
        </div>
        <Preview doc={it.preview} />
      </div>
      {it.status === "pending" && (
        <div className="flex gap-2 shrink-0">
          <Btn onClick={() => onRestore(it)} disabled={busy} data-testid={`recovery-restore-${it.id}`}><RotateCcw className="w-4 h-4" /> {it.op === "delete" ? "Recover" : "Undo edit"}</Btn>
          <Btn variant="ghost" onClick={() => onKeep(it)} disabled={busy} data-testid={`recovery-keep-${it.id}`}><Check className="w-4 h-4" /> Keep permanently</Btn>
        </div>
      )}
    </div>
  </Card>
);

export default function AdminRecovery() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [op, setOp] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await endpoints.adminRecovery({ status: tab, op }); setData(d); } catch (e) { handleErr(e); }
  }, [tab, op]);
  useEffect(() => { load(); }, [load]);

  if (!user?.isSuperAdmin) return <Empty text="Only the Super Admin can open Recovery." />;

  const restore = async (it) => {
    if (!window.confirm(it.op === "delete" ? `Recover "${it.summary}"?` : `Undo this edit and put back the previous version of "${it.summary}"?`)) return;
    setBusy(true);
    try { await endpoints.adminRecoveryRestore(it.id); toast.success("Restored"); load(); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const keep = async (it) => {
    if (!window.confirm(`Keep this ${it.op === "delete" ? "delete" : "edit"} permanently? It can no longer be recovered.`)) return;
    setBusy(true);
    try { await endpoints.adminRecoveryKeep({ ids: [it.id] }); toast.success("Kept permanently"); load(); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const keepAll = async () => {
    if (!window.confirm("Keep ALL listed changes permanently? None of them can be recovered afterwards.")) return;
    setBusy(true);
    try { const { data: d } = await endpoints.adminRecoveryKeep({ all: true }); toast.success(`${d.kept} change(s) kept permanently`); load(); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const chip = (on) => `h-9 px-4 rounded-full text-sm font-semibold transition-colors ${on ? "bg-[#E11D2A] text-white" : "bg-[#121620] border border-[#273042] text-slate-300 hover:text-white"}`;

  return (
    <div data-testid="admin-recovery">
      <PageHeader title="Recovery" desc="Every edit and delete made by admins & staff is saved here. Recover it, undo it, or keep it permanently.">
        {tab === "pending" && (data?.items?.length || 0) > 0 && <Btn variant="ghost" onClick={keepAll} disabled={busy} data-testid="recovery-keep-all"><Check className="w-4 h-4" /> Keep all permanently</Btn>}
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={chip(tab === k)} data-testid={`recovery-tab-${k}`}>{l}{data?.counts?.[k] != null ? ` (${data.counts[k]})` : ""}</button>)}
        <select value={op} onChange={(e) => setOp(e.target.value)} data-testid="recovery-op-filter"
          className="h-9 px-3 rounded-full bg-[#121620] border border-[#273042] text-slate-200 text-sm">
          <option value="">Edits & deletes</option><option value="delete">Deletes only</option><option value="edit">Edits only</option>
        </select>
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <Empty text="Nothing here." /> : (
        <div className="space-y-3">{data.items.map((it) => <Item key={it.id} it={it} onRestore={restore} onKeep={keep} busy={busy} />)}</div>
      )}
      {busy && <Loader2 className="w-5 h-5 animate-spin text-slate-400 mt-4" />}
    </div>
  );
}
